import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BadgeColour, ComponentsModule, DialogService } from '@mastechnicaegroup/carelinelive-ui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Activity } from '@lib/api/models/activity';
import { AppDatePipe } from '@shared/pipes/date.pipe';

interface ChangedField {
    key: string;
    label: string;
    oldValue: string;
    newValue: string;
}

@Component({
    selector: 'app-view-commissioner-audit-entry-dialog',
    imports: [
        CommonModule,
        ComponentsModule,
        AppDatePipe,
    ],
    templateUrl: './view-commissioner-audit-entry-dialog.component.html',
    standalone: true,
    styleUrl: './view-commissioner-audit-entry-dialog.component.scss'
})
export class ViewCommissionerAuditEntryDialogComponent {

    protected readonly entry = inject<Activity>(DIALOG_DATA);
    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    protected readonly showProperties = signal(false);

    protected toggleProperties(): void {
        this.showProperties.update(v => !v);
    }

    protected eventSubtitle(event: string | null): string {
        switch (event) {
            case 'created':           return 'Commissioner Created';
            case 'updated':           return 'Commissioner Updated';
            case 'deleted':           return 'Commissioner Deleted';
            case 'user_assigned':     return 'User Assigned';
            case 'user_removed':      return 'User Removed';
            case 'user_role_updated': return 'User Role Updated';
            default:                  return (event ?? 'unknown')
                .split('_')
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');
        }
    }

    protected eventBadgeColour(event: string | null): BadgeColour {
        switch (event) {
            case 'created':
            case 'user_assigned':     return 'success';
            case 'updated':
            case 'user_role_updated': return 'primary';
            case 'deleted':
            case 'user_removed':      return 'error';
            default:                  return 'variant';
        }
    }

    protected eventIcon(event: string | null): string {
        switch (event) {
            case 'created':           return 'add';
            case 'updated':           return 'save';
            case 'deleted':           return 'delete';
            case 'user_assigned':     return 'person_add';
            case 'user_removed':      return 'person_remove';
            case 'user_role_updated': return 'assignment_ind';
            default:                  return 'edit';
        }
    }

    protected eventLabel(event: string | null): string {
        switch (event) {
            case 'created':           return 'CREATED';
            case 'updated':           return 'UPDATED';
            case 'deleted':           return 'DELETED';
            case 'user_assigned':     return 'USER ASSIGNED';
            case 'user_removed':      return 'USER REMOVED';
            case 'user_role_updated': return 'ROLE UPDATED';
            default:                  return (event ?? 'UNKNOWN').replace(/_/g, ' ').toUpperCase();
        }
    }

    protected specialHourDate(): string | null {
        const attributes = this.entry.properties?.attributes;
        if (!attributes || typeof attributes !== 'object') {
            return null;
        }
        const first = Object.values(attributes as Record<string, unknown>)[0];
        if (first && typeof first === 'object' && !Array.isArray(first) && 'date' in (first as object)) {
            return (first as Record<string, unknown>)['date'] as string;
        }
        return null;
    }

    protected causerDisplay(): string {
        const causer = this.entry.causer;
        if (!causer) {
            return '—';
        }
        return causer.name ? `${causer.name} (${causer.email})` : causer.email;
    }

    protected changedFields(): ChangedField[] {
        const attributes = this.entry.properties?.attributes;
        const old = this.entry.properties?.old ?? {};

        if (!attributes || typeof attributes !== 'object') {
            return [];
        }

        return Object.entries(attributes).map(([key, value]) => ({
            key,
            label: key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()),
            oldValue: this.formatPropertyValue(old[key]),
            newValue: this.formatPropertyValue(value),
        }));
    }

    private formatPropertyValue(value: unknown): string {
        if (value == null) {
            return '—';
        }
        if (typeof value === 'object' && !Array.isArray(value)) {
            const v = value as Record<string, unknown>;
            if ('number' in v && 'type' in v) {
                return `${v['number']} (${v['type']})`;
            }
            if ('start_time' in v && 'end_time' in v && 'open' in v) {
                return `${v['start_time']} – ${v['end_time']} (${v['open'] ? 'Open' : 'Closed'})`;
            }
            if ('start_time' in v && 'end_time' in v) {
                return `${v['start_time']} – ${v['end_time']}`;
            }
            if ('open' in v) {
                return v['open'] ? 'Open' : 'Closed';
            }
            return Object.entries(v).map(([k, val]) => `${k}: ${val}`).join(', ');
        }
        if (Array.isArray(value)) {
            return value.map(item => this.formatPropertyValue(item)).join(', ');
        }
        return String(value);
    }

    public static show(dialog: DialogService, data: Activity) {
        return dialog.openDialog<DialogRef, Activity>(ViewCommissionerAuditEntryDialogComponent, {
            data
        });
    }
}
